const TOKEN_KEY = "aiptyg.agent.session.v1";
declare const __AGENT_API_BASE_URL__: string | undefined;

const agentApiBaseUrl = (typeof __AGENT_API_BASE_URL__ === "string" ? __AGENT_API_BASE_URL__ : "").replace(/\/+$/, "");

export function agentApiUrl(path: string) {
  return `${agentApiBaseUrl}/v1/agent${path}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
const errorMessages: Record<string, string> = {
  insufficient_balance: "可用余额不足，请联系平台充值后再提交。已保存的草稿会保留。",
  invalid_finance_input: "资金信息校验失败，请检查金额、名额后重试",
  finance_conflict: "资金或结算状态已更新，请刷新后重试",
  message_import_required: "请先导入收件人（手机号码或邮箱）和 iMessage 内容，再提交审核",
  invalid_advertising_input: "广告内容校验失败，请检查素材、链接、渠道参数和排期；报表日期范围不能超过 366 天",
  advertising_permission_required: "广告权限未开通，请联系平台管理员",
  advertising_disabled: "广告权限未开通，请联系平台管理员",
  advertising_unavailable: "广告服务暂不可用，请稍后重试",
  invalid_credentials: "账号或密码不正确",
  invalid_input: "内容校验失败，请检查任务类型、截图要求与应用配置",
  invalid_report_filter: "请选择不超过 93 天的有效查询范围",
  platform_unavailable: "平台服务暂不可用，请稍后重试",
  password_must_be_12_to_72_bytes: "新密码需为 12 至 72 字节，请缩短后重试",
  login_rate_limited: "登录尝试过于频繁，请稍后重试",
  unauthorized: "登录已失效，请重新登录",
  session_expired: "登录已过期，请重新登录",
  password_change_required: "请先修改初始密码",
  account_disabled: "账号已停用，请联系总后台",
  agent_login_disabled: "商户登录权限已停用",
  business_frozen: "业务已冻结，请联系总后台",
  publish_restricted: "发布权限受限，请联系总后台",
  version_conflict: "内容已被更新，请刷新后重试",
  conflict: "内容已被更新，请刷新后重试",
  invalid_password: "原密码不正确",
  weak_password: "新密码强度不足，请使用至少 12 位并包含大小写字母、数字和符号",
  task_amount_out_of_range: "金额超出平台允许区间，请重新加载并按当前价格范围修改",
  invalid_payload: "任务内容校验失败，请检查必填项",
  not_found: "内容不存在或您没有访问权限",
  publisher_unavailable: "任务发布服务暂不可用，请稍后重试",
  forbidden: "您暂时没有执行此操作的权限",
  storage_unavailable: "素材存储暂不可用，请联系平台配置存储后重试",
  storage_not_configured: "平台尚未配置素材存储，请联系平台管理员",
  upload_failed: "图片上传失败，请稍后重试",
  file_too_large: "图片不能超过 10 MiB，请压缩后重试",
  unsupported_image: "仅支持 PNG、JPEG、GIF、WebP 图片，不支持 SVG",
  invalid_image: "图片内容无效或已损坏，请重新选择",
  image_too_large: "图片像素不能超过 2400 万，请缩小图片尺寸",
  invalid_module: "请选择任务图标或流程图分类",
  material_storage_not_configured: "平台尚未配置素材存储，请联系平台管理员后重试",
};
export const session = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (token: string) => sessionStorage.setItem(TOKEN_KEY, token),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};
export function query(values: Record<string, unknown> = {}) {
  const s = new URLSearchParams();
  Object.entries(values).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  });
  return s.size ? `?${s}` : "";
}
export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = session.get();
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  if (token && !headers.has("Authorization"))
    headers.set("Authorization", `Bearer ${token}`);
  let response: Response;
  try {
    response = await fetch(agentApiUrl(path), {
      ...options,
      credentials: "same-origin",
      headers,
    });
  } catch {
    throw new ApiError(0, "network_error", "连接服务失败，请检查网络后重试");
  }
  checkSession(response.status, token);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError(
      response.status,
      "invalid_response",
      "服务返回异常，请稍后重试",
    );
  }
  if (!response.ok) {
    throw responseError(path, response.status, body);
  }
  return body as T;
}
function checkSession(status: number, token: string | null) {
  if (status === 401 && token) {
    session.clear();
    window.dispatchEvent(new Event("agent:unauthorized"));
  }
}
function responseError(path: string, status: number, body: unknown) {
  const error = (body && typeof body === "object" ? body : {}) as { error?: string; message?: string };
  if (path.split("?")[0] === "/advertising/creatives" && status === 404)
    return new ApiError(status, error.error || "not_found", "广告素材列表接口暂不可用，请联系平台管理员检查商户服务与平台服务是否已同步更新");
  if (path.startsWith("/advertising") && status === 403 && ["advertising_permission_required", "advertising_disabled"].includes(error.error || ""))
    window.dispatchEvent(new Event("agent:advertising-denied"));
  if (error.error === "password_change_required")
    window.dispatchEvent(new Event("agent:password-required"));
  return new ApiError(status, error.error || "request_failed",
    (path.startsWith("/advertising") && ["invalid_input", "invalid_payload", "invalid_advertising_input"].includes(error.error || "") ? errorMessages.invalid_advertising_input : path === "/auth/login" && status === 401 ? errorMessages.invalid_credentials : errorMessages[error.error || ""]) ||
    (path.startsWith("/materials") && error.error === "internal_error" ? "素材服务暂不可用，请稍后刷新列表确认结果" : "") ||
    error.message || `请求失败（${status}），请稍后重试`);
}
/** Multipart with real transport progress; completion still requires a successful server response. */
export function upload<T>(path: string, body: FormData, onProgress?: (percent: number) => void, signal?: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError(0, "upload_aborted", "已取消上传"));
      return;
    }
    const xhr = new XMLHttpRequest();
    const token = session.get();
    const abort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener("abort", abort);
    xhr.open("POST", agentApiUrl(path));
    xhr.timeout = 120_000;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round(event.loaded / event.total * 100));
    };
    xhr.onload = () => {
      cleanup();
      checkSession(xhr.status, token);
      let result: unknown;
      try { result = JSON.parse(xhr.responseText); }
      catch {
        reject(new ApiError(xhr.status, "invalid_response", "服务返回异常，请稍后重试"));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) reject(responseError(path, xhr.status, result));
      else resolve(result as T);
    };
    xhr.onerror = () => { cleanup(); reject(new ApiError(0, "network_error", "连接服务失败，请检查网络后重试")); };
    xhr.ontimeout = () => { cleanup(); reject(new ApiError(0, "upload_timeout", "上传超时，请刷新素材列表确认结果后重试")); };
    xhr.onabort = () => { cleanup(); reject(new ApiError(0, "upload_aborted", "已取消上传")); };
    signal?.addEventListener("abort", abort, { once: true });
    xhr.send(body);
  });
}
export const post = <T>(path: string, body: unknown = {}) =>
  api<T>(path, { method: "POST", body: JSON.stringify(body) });
export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PUT", body: JSON.stringify(body) });

/** Private evidence stays behind authenticated API reads, never in storage URLs. */
export async function fetchPrivateImage(path: string, signal: AbortSignal): Promise<Blob> {
  const token = session.get();
  const response = await fetch(agentApiUrl(path), { signal, cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {} });
  checkSession(response.status, token);
  if (!response.ok) throw new Error(response.status === 404 || response.status === 403 ? '此凭证已不可用或没有访问权限' : '凭证加载失败，请重试');
  if (!/^image\/(png|jpeg|gif|webp)(;|$)/i.test(response.headers.get('Content-Type') || '')) throw new Error('凭证格式无效');
  return response.blob();
}
